import { SelectorEntradas } from "./SelectorEntradas";
import { useEvento } from "@/store/evento/evento";
import { useReservedStock } from "../../hooks/useReservedStock";
import { useStockConflict } from "../../hooks/useStockConflict";
import { AlertCircle } from "lucide-react";
import { EntradasSeleccionadas } from "./EntradasSeleccionadas";

type Props = {
    funcionId: string;
    maxEntradas: boolean;
}

/**
 * Layout para eventos sin mapa.
 * Muestra: selector de entradas a la izquierda, cards de seleccionadas a la derecha.
 */
export function LayoutSinMapa({
    funcionId,
}: Props) {
    const { state: entradas } = useEvento.use.entradas();

    // Fetch reserved stock for this specific function
    const { stock: stockReservado, loading: loadingStock } = useReservedStock(funcionId);

    // Detect stock conflicts (when selected > available)
    const { hasConflict, conflicts, totalExceso } = useStockConflict(entradas, stockReservado);

    return (
        <div className="flex flex-col gap-4 w-full">
            {/* Alert de conflicto de stock */}
            {hasConflict && (
                <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-4 animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="text-amber-400 flex-shrink-0 mt-0.5" size={20} />
                        <div className="flex-1">
                            <h4 className="text-amber-200 font-semibold mb-2">
                                Stock insuficiente
                            </h4>
                            <p className="text-amber-100/90 text-sm mb-3">
                                Otras personas confirmaron sus compras. Por favor, reduce tu selección:
                            </p>
                            <ul className="space-y-1 text-sm">
                                {conflicts.map(conflict => (
                                    <li key={conflict.tipoEntradaId} className="text-amber-100/80">
                                        <span className="font-medium text-amber-200">{conflict.nombre}</span>:
                                        Tienes <span className="font-semibold">{conflict.seleccionadas}</span> seleccionadas,
                                        pero solo hay <span className="font-semibold">{conflict.disponible}</span> disponibles.
                                        Quita <span className="font-semibold text-amber-300">{conflict.exceso}</span>.
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex flex-col gap-4 xl:flex-row xl:items-start justify-center h-full">
                {/* Columna izquierda: Selector de entradas */}
                <div className="w-full xl:basis-[50%] xl:pr-4">
                    <h2 className="text-white text-xl md:text-2xl font-display text-center mb-6">
                        Seleccioná tus entradas
                    </h2>
                    <SelectorEntradas
                        stockReservado={stockReservado}
                    />
                </div>

                {/* Columna derecha: Cards de entradas seleccionadas */}
                <div className="w-full xl:basis-[50%] xl:pl-4">
                    {entradas.length > 0 ? (
                        <>
                            <h3 className="text-white text-xl md:text-2xl font-display text-center mb-6">
                                Tus Entradas
                            </h3>
                            <EntradasSeleccionadas />
                        </>
                    ) : (
                        <div className="hidden xl:flex items-center justify-center h-full">
                            <p className="text-white/40 text-center">
                                Seleccioná entradas para continuar
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
